/**
 * Iter53 P34 — Extras din SaleOverview.jsx (self-contained).
 *
 * Randează istoricul REAL CRM al unei vânzări:
 *   - audit log (sale_create, contract_update, supplier_field_manual_set, …)
 *   - field changes (POD, NLC, business_status, notes_bo, …)
 *   - status transitions (old_status → new_status)
 *
 * Sursa datelor: GET /api/sales/{id}/activity?limit=200
 * Zero prop drilling — nu depinde de state-ul din SaleOverview.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS_RO = {
  sale_create: { label: "Vânzare creată", icon: "▸", tone: "green" },
  sale_update: { label: "Vânzare actualizată", icon: "✎", tone: "blue" },
  contract_create: { label: "Contract creat", icon: "▸", tone: "green" },
  contract_update: { label: "Contract actualizat", icon: "✎", tone: "blue" },
  client_create: { label: "Client creat", icon: "▸", tone: "green" },
  client_update: { label: "Client actualizat", icon: "✎", tone: "blue" },
  supplier_field_manual_set: { label: "Status furnizor modificat manual", icon: "✎", tone: "amber" },
  status_change: { label: "Status schimbat", icon: "↻", tone: "amber" },
  contract_status_create: { label: "Status contract inițializat", icon: "▸", tone: "green" },
  contract_status_update: { label: "Status contract actualizat", icon: "↻", tone: "amber" },
  sale_import: { label: "Import date", icon: "⤓", tone: "indigo" },
  import_easy: { label: "Import Easy", icon: "⤓", tone: "indigo" },
  import_salesforce: { label: "Import Salesforce", icon: "⤓", tone: "indigo" },
  import_crm: { label: "Import CRM Legacy", icon: "⤓", tone: "indigo" },
  import_crm_legacy: { label: "Import CRM Legacy", icon: "⤓", tone: "indigo" },
  withdraw: { label: "Retragere marcată", icon: "✕", tone: "red" },
  clear_withdraw: { label: "Retragere anulată", icon: "↶", tone: "slate" },
  field_change: { label: "Câmp modificat", icon: "✎", tone: "blue" },
};

const FIELD_LABELS_RO = {
  supplier_status: "Status furnizor",
  supplier_status_date: "Data status furnizor",
  csd_date: "Data CSD",
  signed_date: "Data semnării",
  activation_date: "Data activării",
  business_status: "Status SF",
  internal_status: "Status intern",
  notes_bo: "Note backoffice",
  notes_agent: "Note agent",
  product_kind: "Tip produs",
  product_name: "Produs",
  contract_number: "Nr. contract",
  pod: "POD",
  nlc: "NLC",
  case_record_type: "Tip proces SF",
};

const toneClass = (tone) => {
  switch (tone) {
    case "green":  return "bg-emerald-500";
    case "blue":   return "bg-blue-500";
    case "amber":  return "bg-amber-500";
    case "red":    return "bg-red-500";
    case "indigo": return "bg-indigo-500";
    case "slate":  return "bg-slate-400";
    default:       return "bg-slate-300";
  }
};

const formatFieldValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
};

const renderEventDetails = (ev) => {
  if (ev.kind === "field_change") {
    const f = ev.details?.field;
    const label = FIELD_LABELS_RO[f] || f;
    const old = ev.details?.old ?? "—";
    const next = ev.details?.new ?? "—";
    return (
      <span>
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-400 mx-1">:</span>
        <span className="text-slate-500 line-through">{String(old).slice(0, 60)}</span>
        <span className="text-slate-400 mx-1.5">→</span>
        <span className="text-slate-900 font-medium">{String(next).slice(0, 60)}</span>
      </span>
    );
  }
  if (ev.kind === "status_change") {
    const old = ev.details?.old_status ?? ev.details?.old;
    const next = ev.details?.new_status ?? ev.details?.new;
    return (
      <span>
        <span className="text-slate-500">{old || "—"}</span>
        <span className="text-slate-400 mx-1.5">→</span>
        <span className="text-slate-900 font-medium">{next || "—"}</span>
      </span>
    );
  }
  const reason = ev.details?.reason;
  const changes = ev.details?.changes;
  const before = ev.details?.before;
  const after = ev.details?.after;
  const rows = [];
  if (Array.isArray(changes)) {
    for (const c of changes) {
      rows.push({ field: c.field, old: c.old, new: c.new });
    }
  } else if (before && after && typeof before === "object" && typeof after === "object") {
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    for (const k of keys) {
      if (before[k] !== after[k]) {
        rows.push({ field: k, old: before[k], new: after[k] });
      }
    }
  }
  if (rows.length > 0) {
    return (
      <div className="space-y-0.5">
        {rows.map((r, idx) => (
          <div key={`${r.field}-${idx}`} data-testid={`event-diff-${r.field}`}>
            <span className="font-medium text-slate-700">{FIELD_LABELS_RO[r.field] || r.field}</span>
            <span className="text-slate-400 mx-1">:</span>
            <span className="text-slate-500 line-through">{formatFieldValue(r.old)}</span>
            <span className="text-slate-400 mx-1.5">→</span>
            <span className="text-slate-900 font-medium">{formatFieldValue(r.new)}</span>
          </div>
        ))}
        {reason && <div className="text-slate-500 italic mt-1">motiv: {reason}</div>}
      </div>
    );
  }
  const changedFields = ev.details?.changed_fields;
  const parts = [];
  if (changedFields && Array.isArray(changedFields) && changedFields.length) {
    parts.push(`câmpuri: ${changedFields.map((f) => FIELD_LABELS_RO[f] || f).join(", ")}`);
  }
  if (reason) parts.push(`motiv: ${reason}`);
  return parts.length > 0 ? <span className="text-slate-600">{parts.join(" · ")}</span> : null;
};

const FILTER_TABS = [
  { key: "all",    label: "Toate",        tone: "slate" },
  { key: "manual", label: "Doar manuale", tone: "amber" },
  { key: "import", label: "Doar import",  tone: "indigo" },
  { key: "status", label: "Doar status",  tone: "blue" },
];

// Iter64a — clasificarea vine din backend (ev.provenance: import | manual | system),
// aceeași regulă ca în tab-ul Activitate; fallback local identic pentru răspunsuri vechi.
const IMPORT_SOURCES = new Set(["easy", "salesforce", "sf", "crm", "crm_legacy", "legacy", "import"]);
export const provenanceOf = (ev) => {
  if (ev.provenance) return ev.provenance;
  const src = (ev.source || "").toLowerCase();
  const action = (ev.action || "").toLowerCase();
  if (ev.kind === "import" || src.startsWith("import_") || IMPORT_SOURCES.has(src) || action.startsWith("import_")) return "import";
  return "manual";
};

export const matchesFilter = (ev, f) => {
  if (f === "all") return true;
  if (f === "import") return provenanceOf(ev) === "import";
  if (f === "manual") return provenanceOf(ev) === "manual";
  if (f === "status") return ev.kind === "status_change";
  return true;
};

export const CrmActivityTimeline = ({ saleId }) => {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!saleId) return undefined;
    const ctrl = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/sales/${saleId}/activity?limit=200`, { signal: ctrl.signal });
        setEvents(r.data?.events || []);
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => ctrl.abort();
  }, [saleId]);

  if (loading) {
    return <p className="text-xs text-slate-400 italic py-3" data-testid="timeline-loading">Se încarcă istoricul…</p>;
  }
  if (!events || events.length === 0) {
    return <p className="text-xs text-slate-400 italic py-3" data-testid="timeline-empty">Nu există încă evenimente înregistrate.</p>;
  }

  const counts = {
    all: events.length,
    manual: events.filter((e) => matchesFilter(e, "manual")).length,
    import: events.filter((e) => matchesFilter(e, "import")).length,
    status: events.filter((e) => matchesFilter(e, "status")).length,
  };
  const filtered = filter === "all" ? events : events.filter((e) => matchesFilter(e, filter));
  const visible = showAll ? filtered : filtered.slice(0, 12);

  return (
    <div data-testid="sale-timeline-crm-wrapper">
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="timeline-filters">
        {FILTER_TABS.map((t) => {
          const active = filter === t.key;
          const n = counts[t.key];
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => { setFilter(t.key); setShowAll(false); }}
              disabled={n === 0 && t.key !== "all"}
              className={
                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition " +
                (active
                  ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                  : (n === 0 && t.key !== "all"
                      ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                      : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:text-indigo-700"))
              }
              data-testid={`timeline-filter-${t.key}`}
            >
              {t.label}
              <span className={
                "inline-flex items-center justify-center min-w-[1.25rem] px-1 h-4 rounded-full text-[10px] font-semibold " +
                (active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600")
              }>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-3" data-testid="timeline-filter-empty">
          Niciun eveniment pentru filtrul selectat.
        </p>
      ) : (
        <ol className="pt-1 pb-1 space-y-3" data-testid="sale-timeline-crm">
          {visible.map((ev, i) => {
            const meta = ACTION_LABELS_RO[ev.action] || ACTION_LABELS_RO[ev.kind]
              || (ev.kind === "import" ? { label: ev.label || "Import", icon: "⇪", tone: "indigo" } : { label: ev.action || ev.kind, icon: "•", tone: "slate" });
            const isLast = i === visible.length - 1;
            const when = ev.when ? new Date(ev.when) : null;
            const dateStr = when ? when.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }) : "";
            return (
              <li key={`${ev.when}-${i}`} className="relative pl-7" data-testid={`timeline-event-${ev.kind}`}>
                {!isLast && <span className="absolute left-[10px] top-5 bottom-[-12px] w-px bg-slate-200" />}
                <span className={`absolute left-1.5 top-1.5 h-3.5 w-3.5 rounded-full ${toneClass(meta.tone)} ring-2 ring-white shadow-sm`} />
                <div className="text-xs text-slate-500">{dateStr}</div>
                <div className="mt-0.5 text-sm text-slate-900">
                  <span className="font-medium">{meta.label}</span>
                  {ev.actor_name && (
                    <span className="text-slate-500"> · de către {ev.actor_name}</span>
                  )}
                  {ev.source && ev.source !== "manual" && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                      {ev.source}
                    </span>
                  )}
                  {ev.kind === "import" && (
                    <span
                      className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${ev.reconstructed ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
                      title={ev.reconstructed
                        ? "Derivat din metadatele vânzării (first_imported_at + lot). Lotul nu a înregistrat rezultatul acestui rând — nu este dovada completă a procesării."
                        : "Înregistrat de import la momentul operațiunii (rezultat pe rând salvat pe lot)."}
                      data-testid={ev.reconstructed ? "timeline-import-reconstructed" : "timeline-import-recorded"}
                    >
                      {ev.reconstructed ? "reconstruit din metadate" : "înregistrat la import"}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{renderEventDetails(ev)}</div>
              </li>
            );
          })}
          {filtered.length > 12 && (
            <li className="pl-7 pt-1">
              <button
                type="button"
                className="text-xs text-indigo-600 hover:text-indigo-700 underline"
                onClick={() => setShowAll((v) => !v)}
                data-testid="timeline-toggle-all"
              >
                {showAll
                  ? `Arată doar ultimele 12 evenimente`
                  : `Arată toate (${filtered.length} evenimente)`}
              </button>
            </li>
          )}
        </ol>
      )}
    </div>
  );
};

export default CrmActivityTimeline;
